'use client';

import SanityImage from '@sanity-image';
import RichText from 'components/rich-text';
import MarkdownParser from 'components/rich-text/markdown-parser';
import { Link } from 'src/navigation';
import { getUrlString } from 'helpers/helpers';
import RedundantLink from 'components/accesibility/redundant-link';
import styles from './content-card.module.scss';
import { useLocale } from 'next-intl';
import Badge from 'components/badge';
import { type ReactNode } from 'react';
import { type SanityInternalLink } from '@metmuseum/types';
import { type Image } from '@metmuseum/types';
import { Eyebrow } from 'components/eyebrow';
import useAnimateIntoView from 'hooks/use-animate-into-view';
import cn from 'classnames';
import hoverStyles from '../hover-state.module.scss';

const ContentCard = ({
	title,
	badges,
	description,
	textDescription,
	image,
	link,
	internalLink,
	eyebrow,
	children, //Optional Children to appear below Description
	className,
	openLinkInNewTab = false,
	style = {},
	sideBySide = false,
	partners = [],
	analytics,
}: ContentCardProps) => {
	const lang = useLocale();
	const cardLink = getUrlString({ link, internalLink }) || null;
	const linkTarget = openLinkInNewTab ? '_blank' : undefined;
	const classString = [
		styles.card,
		className ?? '',
		sideBySide ? styles.sideBySide : '',
	].join(' ');

	const {
		ref: animateRef,
		className: animateClassName,
		style: animateStyle,
	} = useAnimateIntoView();

	return (
		<div
			style={{ ...style, ...animateStyle }}
			className={cn(classString, animateClassName, hoverStyles.card)}
			ref={animateRef}
		>
			<div className={`${styles.imageWrapper}`}>
				{cardLink && (
					<RedundantLink
						locale={lang}
						href={cardLink}
						target={linkTarget}
						Content_Type={analytics?.content_type}
						Content_Name={analytics?.content_name}
						Content_Block={analytics?.content_block}
						event={analytics?.event}
					/>
				)}
				{image && (
					<SanityImage
						image={image}
						fill={true}
						sizes="(max-width: 960px) 100vw, 50vw"
					/>
				)}
				{!!badges?.length && (
					<div className={styles.badges}>
						{badges.map((badge) => {
							return (
								<Badge
									color={badge?.color || 'currentColor'}
									key={badge.text}
									text={badge.text}
									icon={badge.icon}
								/>
							);
						})}
					</div>
				)}
			</div>

			<div className={styles.body}>
				<Eyebrow text={eyebrow} />
				<div className={styles.heading} role="heading" aria-level={2}>
					{cardLink ? (
						<Link locale={lang} href={cardLink} target={linkTarget}>
							<MarkdownParser>{title}</MarkdownParser>
						</Link>
					) : (
						<MarkdownParser>{title}</MarkdownParser>
					)}
				</div>
				<div>
					{!!description?.length && (
						<div className={styles.description}>
							<RichText value={description} />
						</div>
					)}
					{!!textDescription && (
						<div className={styles.description}>
							<MarkdownParser>{textDescription}</MarkdownParser>
						</div>
					)}
					{!!partners?.length && (
						<div className={styles.partners}>
							<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
								{partners.map((partner) => {
									const partnerContent = (
										<div style={{ display: 'inline-block' }}>
											<SanityImage
												image={partner.partnerImage}
												sizes="160px"
												alt={`The logo of ${partner.displayName}`}
												style={{ height: '40px', width: 'auto' }}
											/>
										</div>
									);

									return partner?.link?.href ? (
										<Link key={partner._id} href={partner.link.href} passHref>
											{partnerContent}
										</Link>
									) : (
										<div key={partner._id}>{partnerContent}</div>
									);
								})}
							</div>
						</div>
					)}
					{children}
				</div>
			</div>
		</div>
	);
};

export interface ContentCardProps {
	children?: ReactNode[] | ReactNode;
	className?: string;
	description?: any[] | string; //Portable Text or plaintext
	textDescription?: string;
	eyebrow?: string;
	hasBorder?: boolean;
	image?: Image;
	badges?: { text: string; icon?: any; color?: string }[];
	internalLink?: SanityInternalLink;
	link?: string;
	openLinkInNewTab?: boolean;
	title?: string;
	style?: object;
	sideBySide?: boolean;
	partners?: any[];
	analytics?: {
		content_name?: string;
		content_type?: string;
		content_block?: string;
		event?: string;
	};
}

export default ContentCard;
